import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Card } from "@/components/ui";
import { TruncatedText, focusRingInsetClass } from "@/components/ui";
import { BookingCancellationSource } from "./BookingCancellationSource";
import { BookingCustomerAvatar } from "./BookingCustomerAvatar";
import { BookingPartyItemsCell } from "./BookingPartyItemsCell";
import { PartnerBookingMobileBody } from "./PartnerBookingMobileBody";
import {
  partnerBookingMobileChrome,
  partnerBookingStatusBorderWidth,
  partnerHighlightCardClass,
} from "./partnerBookingChrome";
import {
  getPartnerBookingCardPresentation,
  getPartnerVisitDateTimeLabels,
} from "./partnerBookingCardPresentation";
import { partnerBookingStatusPaid } from "./partnerBookingStatusPaid";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";

interface BookingRowCardProps {
  booking: BookingListRow;
  detailPath: string;
  /** Hide visit date when the surrounding day section already shows it. */
  omitVisitDate?: boolean;
}

export function BookingRowCard({
  booking,
  detailPath,
  omitVisitDate = false,
}: BookingRowCardProps) {
  const { t } = useTranslation("bookings");

  const { dateLabel, shortDateLabel, timeLabel } =
    getPartnerVisitDateTimeLabels(booking);
  const { statusAccent, titleMuted, highlightCard } =
    getPartnerBookingCardPresentation(booking);
  const { statusBadge, paidAmount } = partnerBookingStatusPaid(booking);

  const cancellationSource =
    booking.status === "CANCELED" && booking.cancellationSource ? (
      <BookingCancellationSource source={booking.cancellationSource} />
    ) : null;

  const linkClass = cn(
    "block transition-colors hover:bg-surface-50/70 dark:hover:bg-surface-100/40",
    focusRingInsetClass,
  );

  const timeClassName = cn(
    "font-semibold tabular-nums leading-none tracking-tight",
    titleMuted ? "text-surface-400" : "text-surface-900",
  );

  return (
    <Card
      className={cn(
        "overflow-hidden transition-all duration-200",
        partnerBookingStatusBorderWidth,
        partnerBookingMobileChrome,
        statusAccent,
        highlightCard && partnerHighlightCardClass,
        titleMuted && "bg-surface-100/80",
        "hover:border-surface-300 sm:hover:-translate-y-px sm:hover:shadow-elevated",
      )}
    >
      <Link to={detailPath} className={cn(linkClass, "sm:hidden")}>
        <PartnerBookingMobileBody
          booking={booking}
          omitVisitDate={omitVisitDate}
          titleMuted={titleMuted}
          visitDateLabel={dateLabel}
          visitTimeLabel={timeLabel}
          statusBadge={statusBadge}
          paidAmount={paidAmount}
          cancellationSource={cancellationSource}
        />
      </Link>

      <Link
        to={detailPath}
        className={cn(
          linkClass,
          "hidden grid-cols-[4rem_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-4 px-4 py-3 sm:grid",
        )}
      >
        <div className="flex flex-col items-start gap-0">
          <span className={cn("text-lg", timeClassName)}>{timeLabel || "—"}</span>
          {!omitVisitDate ? (
            <span
              className="mt-1 truncate text-xs capitalize text-surface-500"
              title={dateLabel ?? undefined}
            >
              {shortDateLabel}
            </span>
          ) : null}
        </div>

        <div className="flex min-w-0 items-center gap-3">
          <BookingCustomerAvatar
            name={booking.customerDisplayName}
            muted={titleMuted}
          />
          <TruncatedText
            as="p"
            className={cn(
              "min-w-0 text-sm font-semibold",
              titleMuted ? "text-surface-500" : "text-surface-900",
            )}
          >
            {booking.customerDisplayName}
          </TruncatedText>
        </div>

        <BookingPartyItemsCell booking={booking} />

        <TruncatedText
          as="p"
          className={cn(
            "text-sm",
            titleMuted ? "text-surface-400" : "text-surface-600",
          )}
        >
          {booking.activityName || t("unknownActivity")}
        </TruncatedText>

        <div className="text-right">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {statusBadge}
            {cancellationSource}
            {paidAmount}
          </div>
        </div>
      </Link>
    </Card>
  );
}
